import { useEffect, useMemo, useRef, useState } from 'react'
import localForage from 'localforage'
import io from 'socket.io-client'
import * as Y from 'yjs'
import {
  Awareness,
  applyAwarenessUpdate,
  encodeAwarenessUpdate
} from 'y-protocols/awareness'

import dotenv from 'global/environment'

const getSocketBaseUrl = apiBaseUrl => {
  if (!apiBaseUrl || typeof apiBaseUrl !== 'string') return ''
  const trimmed = apiBaseUrl.replace(/\/+$/, '')
  // Most envs point to ".../api/v1" for REST; socket server is at the app root.
  return trimmed.replace(/\/api\/v\d+$/i, '')
}

const base64ToUint8 = b64 => {
  const bin = atob(b64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i += 1) {
    bytes[i] = bin.charCodeAt(i)
  }
  return bytes
}

const uint8ToBase64 = bytes => {
  const chunkSize = 0x8000
  let bin = ''
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize)
    bin += String.fromCharCode(...chunk)
  }
  return btoa(bin)
}

const getUserIdentity = async () => {
  const { user } =
    (await localForage.getItem('globalState')) || {}
  const firstName =
    user?.firstName || user?.FirstName || user?.name || ''
  const id = user?.id || user?.userId || 0

  const colors = [
    '#f97316',
    '#22c55e',
    '#3b82f6',
    '#a855f7',
    '#ef4444',
    '#14b8a6',
    '#eab308'
  ]
  const color = colors[Math.abs(Number(id) || 0) % colors.length]

  return {
    name: firstName || 'User',
    color
  }
}

export const useCoachingSessionCollabDoc = coachingSessionId => {
  const [ready, setReady] = useState(false)
  const [hasRemoteState, setHasRemoteState] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [lastRefresh, setLastRefresh] = useState(null)
  const [userIdentity, setUserIdentity] = useState(null)
  const socketRef = useRef(null)
  const idleTimerRef = useRef(null)
  const savingTimerRef = useRef(null)

  const emitRefresh = ({ coachingSessionId: id, reason }) => {
    const socket = socketRef.current
    if (!socket) return
    const coachingSessionIdNum = Number(id)
    if (
      !coachingSessionIdNum ||
      Number.isNaN(coachingSessionIdNum)
    )
      return
    if (reason !== 'steps' && reason !== 'meta') return

    socket.emit('coachingSession:refresh', {
      coachingSessionId: coachingSessionIdNum,
      reason
    })
  }

  const ydoc = useMemo(() => {
    // Recreate the doc per session id.
    // (The conditional is intentional to satisfy eslint exhaustive-deps.)
    if (coachingSessionId === null) return new Y.Doc()
    return new Y.Doc()
  }, [coachingSessionId])

  const awareness = useMemo(() => {
    return new Awareness(ydoc)
  }, [ydoc])

  useEffect(() => {
    let mounted = true
    setReady(false)
    setHasRemoteState(false)
    setIsSaving(false)
    setLastRefresh(null)
    setUserIdentity(null)

    if (
      !coachingSessionId ||
      Number.isNaN(Number(coachingSessionId))
    ) {
      setReady(true)
      return undefined
    }

    const socketBase = getSocketBaseUrl(dotenv.apiBaseUrl)
    if (!socketBase) {
      // eslint-disable-next-line no-console
      console.error('Socket base URL is not configured.')
      return undefined
    }

    const connect = async () => {
      const identity = await getUserIdentity()
      setUserIdentity(identity)

      const socket = io(`${socketBase}/collab`, {
        withCredentials: true,
        transports: ['websocket']
      })
      socketRef.current = socket

      // Set local awareness (for collaboration cursors)
      awareness.setLocalStateField('user', identity)

      const onState = payload => {
        if (!mounted) return
        if (
          Number(payload?.coachingSessionId) !==
          Number(coachingSessionId)
        ) {
          return
        }

        const state = payload?.state
        if (typeof state === 'string' && state.length > 0) {
          Y.applyUpdate(ydoc, base64ToUint8(state), 'remote')
          setHasRemoteState(true)
        } else {
          setHasRemoteState(false)
        }

        setReady(true)
      }

      const onUpdate = payload => {
        if (
          Number(payload?.coachingSessionId) !==
          Number(coachingSessionId)
        ) {
          return
        }
        const updateB64 = payload?.update
        if (typeof updateB64 !== 'string') return
        Y.applyUpdate(ydoc, base64ToUint8(updateB64), 'remote')
      }

      const onAwareness = payload => {
        if (
          Number(payload?.coachingSessionId) !==
          Number(coachingSessionId)
        ) {
          return
        }
        const updateB64 = payload?.update
        if (typeof updateB64 !== 'string') return
        applyAwarenessUpdate(
          awareness,
          base64ToUint8(updateB64),
          'remote'
        )
      }

      const onAwarenessUpdate = ({
        added,
        updated,
        removed
      }) => {
        const changedClients = added
          .concat(updated)
          .concat(removed)
        const update = encodeAwarenessUpdate(
          awareness,
          changedClients
        )
        socket.emit('coachingSession:awareness', {
          coachingSessionId,
          update: uint8ToBase64(update)
        })
      }

      const onRefresh = payload => {
        if (!mounted) return
        if (
          Number(payload?.coachingSessionId) !==
          Number(coachingSessionId)
        ) {
          return
        }
        const reason = payload?.reason
        if (reason !== 'steps' && reason !== 'meta') return
        setLastRefresh({
          coachingSessionId: Number(coachingSessionId),
          reason,
          at: Date.now()
        })
      }

      const onDocUpdate = (update, origin) => {
        if (origin === 'remote') return

        // Show "Saving…" after user pauses typing briefly.
        if (idleTimerRef.current)
          clearTimeout(idleTimerRef.current)
        if (savingTimerRef.current)
          clearTimeout(savingTimerRef.current)
        setIsSaving(false)

        idleTimerRef.current = setTimeout(() => {
          if (!mounted) return
          setIsSaving(true)
        }, 700)

        savingTimerRef.current = setTimeout(() => {
          if (!mounted) return
          setIsSaving(false)
        }, 2200)

        socket.emit('coachingSession:update', {
          coachingSessionId,
          update: uint8ToBase64(update)
        })
      }

      socket.on('connect', () => {
        socket.emit(
          'coachingSession:join',
          { coachingSessionId },
          () => {}
        )
      })

      socket.on('coachingSession:state', onState)
      socket.on('coachingSession:update', onUpdate)
      socket.on('coachingSession:awareness', onAwareness)
      socket.on('coachingSession:refresh', onRefresh)
      ydoc.on('update', onDocUpdate)
      awareness.on('update', onAwarenessUpdate)

      socket.on('connect_error', err => {
        // eslint-disable-next-line no-console
        console.error(
          'Collab socket connect_error:',
          err?.message || err
        )
      })

      return () => {
        socket.off('coachingSession:state', onState)
        socket.off('coachingSession:update', onUpdate)
        socket.off('coachingSession:awareness', onAwareness)
        socket.off('coachingSession:refresh', onRefresh)
        ydoc.off('update', onDocUpdate)
        awareness.off('update', onAwarenessUpdate)
        socket.disconnect()
      }
    }

    let cleanup = null
    connect().then(fn => {
      cleanup = fn
    })

    return () => {
      mounted = false
      try {
        if (cleanup) cleanup()
      } catch (e) {
        // noop
      }
      socketRef.current = null
      if (idleTimerRef.current)
        clearTimeout(idleTimerRef.current)
      if (savingTimerRef.current)
        clearTimeout(savingTimerRef.current)
      try {
        awareness.destroy()
        ydoc.destroy()
      } catch (e) {
        // noop
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachingSessionId])

  return {
    ydoc,
    awareness,
    ready,
    hasRemoteState,
    isSaving,
    lastRefresh,
    emitRefresh,
    userIdentity
  }
}
